import React, { useState, useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { api } from '../../shared/utils/api';

export default function LeanDrillSummary({ questions, answers, sessionId, onStartAnother }) {
  const [reviewIdx, setReviewIdx] = useState(null);

  // Mark the session complete on first mount. Fail-soft — the user still
  // sees the summary even if the POST errors; we only log to Sentry.
  useEffect(() => {
    if (!sessionId) return;
    let cancelled = false;
    api('/api/questions/practice/complete', {
      method: 'POST',
      body: JSON.stringify({ session_id: sessionId }),
    })
      .then((res) => {
        if (cancelled) return;
        if (res && res.ok === false) {
          throw new Error(res.error || 'complete failed');
        }
      })
      .catch((err) => {
        try {
          Sentry.captureException(err, {
            tags: {
              context: 'drill_session_complete_failure',
              session_id: String(sessionId),
            },
          });
        } catch {
          // never let Sentry failures break the UI
        }
      });
    return () => {
      cancelled = true;
    };
  }, [sessionId]);

  const total = answers.filter(Boolean).length;
  const totalCorrect = answers.filter((a) => a && a.correct).length;
  const pct = total > 0 ? Math.round((totalCorrect / total) * 100) : 0;
  const scoreColour = pct >= 70 ? 'text-green' : pct >= 40 ? 'text-amber' : 'text-red';

  const incorrect = questions
    .map((q, i) => ({ q, a: answers[i] }))
    .filter(({ a }) => a && !a.correct);

  // ── Review view ────────────────────────────────────────────────────────────
  if (reviewIdx !== null && reviewIdx < incorrect.length) {
    const { q, a } = incorrect[reviewIdx];
    const selectedU = (a.selected_answer || '').toUpperCase();
    const correctU = (a.correct_answer || '').toUpperCase();
    const options = q.options || [];
    const hasNext = reviewIdx + 1 < incorrect.length;

    return (
      <div className="max-w-[720px] mx-auto">
        <div className="flex items-center justify-between mb-sp-4">
          <button
            type="button"
            onClick={() => setReviewIdx(null)}
            className="text-sm text-text-muted hover:text-text-primary transition-colors"
          >
            ← Back to summary
          </button>
          <span className="text-xs text-text-muted">
            {reviewIdx + 1} of {incorrect.length} wrong
          </span>
        </div>

        <div className="bg-bg-card rounded-r-xl border border-border-soft overflow-hidden mb-sp-4">
          <div className="px-sp-5 py-sp-3 border-b border-border-soft">
            <span className="text-[11px] font-semibold uppercase tracking-wider text-accent">
              {q.general_topic || q.subject_area || ''}
            </span>
          </div>

          <div className="px-sp-6 py-sp-5">
            {q.scenario && (
              <p className="text-sm text-text-secondary mb-sp-3 pb-sp-3 border-b border-border-soft leading-relaxed">
                {q.scenario}
              </p>
            )}
            <p className="text-[15px] font-semibold text-text-primary mb-sp-4">{q.stem}</p>

            <div className="flex flex-col gap-sp-2 mb-sp-4">
              {options.map((opt) => {
                const letter = (opt.letter || '').toString();
                const letterU = letter.toUpperCase();
                const isCorrect = letterU === correctU;
                const isWrong = letterU === selectedU && selectedU !== correctU;

                let wrapCls = 'border border-border-soft bg-bg-card';
                let badgeNode = null;
                let letterCls = 'bg-bg-surface text-text-muted';

                if (isCorrect) {
                  wrapCls = 'border-2 border-green bg-[rgba(16,185,129,0.04)]';
                  letterCls = 'bg-green text-white';
                  badgeNode = (
                    <span className="text-[10px] font-semibold text-green uppercase tracking-wide flex-shrink-0">
                      Correct answer
                    </span>
                  );
                } else if (isWrong) {
                  wrapCls = 'border-2 border-red bg-[rgba(239,68,68,0.03)]';
                  letterCls = 'bg-red text-white';
                  badgeNode = (
                    <span className="text-[10px] font-semibold text-red uppercase tracking-wide flex-shrink-0">
                      Your answer
                    </span>
                  );
                }

                return (
                  <div
                    key={letter}
                    className={`flex items-center gap-sp-3 px-sp-4 py-sp-3 rounded-r-md ${wrapCls}`}
                  >
                    <span
                      className={`w-[26px] h-[26px] rounded-full flex items-center justify-center text-xs font-semibold flex-shrink-0 ${letterCls}`}
                    >
                      {letter}
                    </span>
                    <p className="text-sm text-text-primary leading-relaxed flex-1">{opt.text}</p>
                    {badgeNode}
                  </div>
                );
              })}
            </div>

            {a.explanation && (
              <div className="border-l-[3px] border-green bg-[rgba(16,185,129,0.05)] px-sp-3 py-sp-2 rounded-[6px] text-sm text-text-primary leading-relaxed mb-sp-3">
                {a.explanation}
              </div>
            )}

            {a.authority && (
              <p className="text-xs text-text-muted">{a.authority}</p>
            )}
          </div>

          <div className="flex items-center justify-between px-sp-6 py-sp-3 border-t border-border-soft">
            <button
              type="button"
              onClick={() => setReviewIdx(null)}
              className="text-sm text-text-muted hover:text-text-primary transition-colors"
            >
              Back to summary
            </button>
            {hasNext ? (
              <button
                type="button"
                onClick={() => setReviewIdx(reviewIdx + 1)}
                className="px-sp-5 py-sp-3 text-sm font-medium bg-accent text-white rounded-r-md hover:bg-accent-hover transition-colors"
              >
                Next wrong answer →
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setReviewIdx(null)}
                className="px-sp-5 py-sp-3 text-sm font-medium bg-accent text-white rounded-r-md hover:bg-accent-hover transition-colors"
              >
                Close
              </button>
            )}
          </div>
        </div>
      </div>
    );
  }

  // ── Summary view ───────────────────────────────────────────────────────────
  return (
    <div>
      <div className="text-center mb-sp-6">
        <h2 className="text-2xl font-bold text-text-primary mb-sp-1">Session complete</h2>
        <p className="text-sm text-text-muted">
          {total} drill{total === 1 ? '' : 's'} answered
        </p>
      </div>

      <div className="grid grid-cols-2 gap-sp-3 mb-sp-6">
        <div className="bg-bg-card rounded-r-xl border border-border-soft px-sp-4 py-sp-4 text-center">
          <p className="text-xs font-medium text-text-muted mb-sp-1">Score</p>
          <p className={`text-[32px] font-bold ${scoreColour}`}>
            {totalCorrect}
            <span className="text-base font-normal text-text-muted"> / {total}</span>
          </p>
        </div>
        <div className="bg-bg-card rounded-r-xl border border-border-soft px-sp-4 py-sp-4 text-center">
          <p className="text-xs font-medium text-text-muted mb-sp-1">Accuracy</p>
          <p className={`text-[32px] font-bold ${scoreColour}`}>{pct}%</p>
        </div>
      </div>

      {incorrect.length > 0 && (
        <div className="bg-bg-card rounded-r-xl border border-border-soft mb-sp-6 overflow-hidden">
          <div className="px-sp-5 py-sp-3 border-b border-border-soft text-[15px] font-semibold text-text-primary">
            Drills you got wrong
          </div>
          {incorrect.map(({ q }, i) => (
            <button
              key={q.id}
              type="button"
              onClick={() => setReviewIdx(i)}
              className="w-full px-sp-5 py-sp-3 flex items-center gap-sp-3 border-b border-border-soft last:border-b-0 hover:bg-bg-page transition-colors text-left"
            >
              <div className="w-2 h-2 rounded-full flex-shrink-0 bg-red" />
              <div className="flex-1 min-w-0">
                <p className="text-[11px] font-semibold uppercase tracking-wider text-accent mb-[2px]">
                  {q.general_topic || q.subject_area || ''}
                </p>
                <p className="text-sm text-text-primary truncate">{q.stem}</p>
              </div>
              <span className="text-xs text-text-muted flex-shrink-0">Review →</span>
            </button>
          ))}
        </div>
      )}

      <div className="flex justify-center gap-sp-3 mb-sp-12">
        <button
          onClick={onStartAnother}
          className="px-sp-5 py-sp-3 text-sm font-medium bg-accent text-white rounded-r-md hover:bg-accent-hover transition-colors"
        >
          Start another session
        </button>
      </div>
    </div>
  );
}
